import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, CreditCard, Check, Star } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SubscriptionPrompt({ onClose }) {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription/status`);
      setSubscriptionStatus(response.data);
      
      // Show prompt if trial expired or subscription expired
      if (response.data.should_show_upgrade_prompt) {
        setShowPrompt(true);
      }
    } catch (error) {
      console.error('Failed to check subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    if (onClose) onClose();
  };

  if (loading || !showPrompt) {
    return null;
  }

  return (
    <Dialog open={showPrompt} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="w-6 h-6 text-yellow-500" />
            {subscriptionStatus?.trial_expired 
              ? 'Your Free Trial Has Ended' 
              : 'Your Subscription Has Expired'}
          </DialogTitle>
          <DialogDescription>
            {subscriptionStatus?.trial_expired
              ? 'Upgrade to continue using Cane Pay and keep your payroll data safe.'
              : 'Renew your subscription to continue accessing all features.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Plan */}
            <Card className="relative border-2 hover:border-primary/50 transition-colors">
              <CardContent className="p-4">
                <div className="text-center mb-4">
                  <h3 className="font-bold text-lg">Basic</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">$19</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Full payroll management</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>SSB calculations</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Export to Excel/PDF</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4" />
                    <span>3-week draft retention</span>
                  </li>
                </ul>
                <Button className="w-full mt-4" variant="outline">
                  Choose Basic
                </Button>
              </CardContent>
            </Card>

            {/* Cloud Plan */}
            <Card className="relative border-2 border-primary">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-white">
                  <Star className="w-3 h-3 mr-1" />
                  Recommended
                </Badge>
              </div>
              <CardContent className="p-4">
                <div className="text-center mb-4">
                  <h3 className="font-bold text-lg">Cloud</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">$39</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Everything in Basic</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Unlimited payroll storage</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Advanced reports</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <Button className="w-full mt-4">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Choose Cloud
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>Contact your administrator to upgrade your subscription.</p>
            <p className="mt-1">
              Email: <a href="mailto:support@canepay.bz" className="text-primary hover:underline">support@canepay.bz</a>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Continue with Limited Access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
